import React from 'react';
import { formatEtat, getEtatColor } from '../utils/etatFormatter';
import LocationDisplaySimple from './LocationDisplaySimple';
import ObjetPhotoThumb from './ObjetPhotoThumb';

interface ObjetCardMobileProps {
  objet: any;
  viewMode: 'liste' | 'card';
  cardLayout?: 'compact' | 'full';
  onEdit: (objet: any) => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canArchive?: boolean;
  canMove?: boolean;
}

const ObjetCardMobile: React.FC<ObjetCardMobileProps> = ({ 
  objet, 
  viewMode,
  cardLayout = 'compact',
  onEdit, 
  canEdit = true, 
  canDelete = true,
  canArchive = true,
  canMove = true
}) => {
  const renderEtatBadge = () => {
    const etatFormatted = formatEtat(objet.etat);
    const colorClasses = getEtatColor(objet.etat);
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorClasses}`}>
        {etatFormatted}
      </span>
    );
  };

  const renderBrouillonBadge = () => {
    if (objet.ficheStatut !== 'brouillon') return null;
    return (
      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
        À compléter
      </span>
    );
  };

  const getLocationData = () => {
    if (objet.box) {
      return {
        site: objet.box.zone?.piece?.batiment?.site || objet.box.piece?.batiment?.site,
        batiment: objet.box.zone?.piece?.batiment || objet.box.piece?.batiment,
        piece: objet.box.zone?.piece || objet.box.piece,
        zone: objet.box.zone,
        box: objet.box
      };
    } else if (objet.zone) {
      return {
        site: objet.zone.piece?.batiment?.site,
        batiment: objet.zone.piece?.batiment,
        piece: objet.zone.piece,
        zone: objet.zone,
        box: null
      };
    }
    return null;
  };

  if (viewMode === 'liste') {
    return (
      <div className="flex items-start gap-3">
        <ObjetPhotoThumb objet={objet} className="w-14 h-14" placeholder="📦" />

        {/* Contenu principal - Layout vertical optimisé */}
        <div className="flex-1 min-w-0 overflow-hidden">
          {/* Ligne 1: Nom + État (horizontal) */}
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0" title={objet.nom}>
              <button
                type="button"
                title={objet.nom}
                className="block w-full truncate text-left text-blue-700 hover:text-blue-900 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(objet);
                }}
                aria-label={`Ouvrir la fiche de ${objet.nom}`}
              >
                {objet.nom}
              </button>
            </h3>
            <div className="flex-shrink-0 flex items-center gap-1">
              {renderBrouillonBadge()}
              {renderEtatBadge()}
            </div>
          </div>

          {/* Ligne 2: Catégorie + Quantité */}
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-xs text-gray-600 truncate flex-1 min-w-0">
              {objet.categorie}{objet.sousCategorie && ` · ${objet.sousCategorie}`}
            </p>
            <span className="text-xs text-gray-500 flex-shrink-0 font-medium">×{objet.quantite}</span>
          </div>

          {/* Ligne 3: Localisation (version verticale pour mobile) */}
          <div className="mb-1.5">
            <div className="flex flex-col gap-0.5">
              {getLocationData()?.site && (
                <span className="text-xs text-gray-600">
                  📍 {getLocationData()?.site?.nom}
                </span>
              )}
              {getLocationData()?.piece && (
                <span className="text-xs text-gray-600">
                  🚪 {getLocationData()?.piece?.nom}
                </span>
              )}
              {(getLocationData()?.zone || getLocationData()?.box) && (
                <span className="text-xs text-gray-600">
                  {getLocationData()?.box ? '📦' : '🏠'} {getLocationData()?.box?.nom || getLocationData()?.zone?.nom}
                </span>
              )}
            </div>
          </div>

          {/* Ligne 4: Badges (Besoin achat + SKU) */}
          <div className="flex items-center gap-2 flex-wrap">
            {objet.besoinAchat && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                🛒
              </span>
            )}
            {objet.sku && (
              <span className="text-xs text-gray-400 font-mono truncate">{objet.sku}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mode card pleine largeur
  if (cardLayout === 'full') {
    return (
      <div
        className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col sm:flex-row ${
          canEdit ? 'cursor-pointer hover:shadow-md' : ''
        } transition-shadow ${objet.besoinAchat ? 'border-l-4 border-l-orange-500 bg-orange-50' : ''}`}
        onClick={canEdit ? () => onEdit(objet) : undefined}
      >
        <ObjetPhotoThumb
          objet={objet}
          className="w-full sm:w-40 h-32 sm:h-auto sm:min-h-[8rem] shrink-0 rounded-none"
          placeholder="📷"
          imgClassName="w-full h-full object-cover"
        />
        <div className="p-4 flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-semibold text-gray-900 truncate flex-1 min-w-0" title={objet.nom}>
              <button
                type="button"
                title={objet.nom}
                className="block w-full truncate text-left text-blue-700 hover:text-blue-900 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(objet);
                }}
                aria-label={`Ouvrir la fiche de ${objet.nom}`}
              >
                {objet.nom}
              </button>
            </h3>
            <div className="flex-shrink-0 flex items-center gap-1">{renderBrouillonBadge()}{renderEtatBadge()}</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-sm text-gray-600">
            <span>
              {objet.categorie}
              {objet.sousCategorie && ` · ${objet.sousCategorie}`}
            </span>
            <span className="font-medium">×{objet.quantite}</span>
            {objet.sku && <span className="font-mono text-xs text-gray-400">{objet.sku}</span>}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
            {getLocationData()?.site && <span>📍 {getLocationData()?.site?.nom}</span>}
            {getLocationData()?.piece && <span>🚪 {getLocationData()?.piece?.nom}</span>}
            {(getLocationData()?.zone || getLocationData()?.box) && (
              <span>
                {getLocationData()?.box ? '📦' : '🏠'}{' '}
                {getLocationData()?.box?.nom || getLocationData()?.zone?.nom}
              </span>
            )}
          </div>
          {objet.besoinAchat && (
            <span className="inline-flex mt-2 items-center px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
              🛒 Besoin achat
            </span>
          )}
        </div>
      </div>
    );
  }

  // Mode card compact (grille)
  return (
    <div 
      className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden ${canEdit ? 'cursor-pointer hover:shadow-md' : ''} transition-shadow ${
        objet.besoinAchat ? 'border-l-4 border-l-orange-500 bg-orange-50' : ''
      }`}
      onClick={canEdit ? () => onEdit(objet) : undefined}
    >
      <ObjetPhotoThumb
        objet={objet}
        className="w-full h-24"
        placeholder="📷"
        imgClassName="w-full h-full object-cover"
      />

      {/* Contenu */}
      <div className="p-3">
        {/* Ligne 1: Nom + État */}
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0" title={objet.nom}>
            <button
              type="button"
              title={objet.nom}
              className="block w-full truncate text-left text-blue-700 hover:text-blue-900 hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(objet);
              }}
              aria-label={`Ouvrir la fiche de ${objet.nom}`}
            >
              {objet.nom}
            </button>
          </h3>
          <div className="flex-shrink-0 flex items-center gap-1">
            {renderBrouillonBadge()}
            {renderEtatBadge()}
          </div>
        </div>

        {/* Ligne 2: Catégorie + Quantité */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-xs text-gray-600 truncate flex-1 min-w-0">
            {objet.categorie}{objet.sousCategorie && ` · ${objet.sousCategorie}`}
          </p>
          <span className="text-xs text-gray-500 flex-shrink-0 font-medium">×{objet.quantite}</span>
        </div>

        {/* Ligne 3: Localisation (icônes verticales) */}
        <div className="mb-2 space-y-1">
          {getLocationData()?.site && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">📍</span>
              <span className="truncate">{getLocationData()?.site?.nom}</span>
            </div>
          )}
          {getLocationData()?.piece && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">🚪</span>
              <span className="truncate">{getLocationData()?.piece?.nom}</span>
            </div>
          )}
          {(getLocationData()?.zone || getLocationData()?.box) && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="flex-shrink-0">{getLocationData()?.box ? '📦' : '🏠'}</span>
              <span className="truncate">{getLocationData()?.box?.nom || getLocationData()?.zone?.nom}</span>
            </div>
          )}
        </div>

        {/* Ligne 4: Badges compacts */}
        <div className="flex items-center gap-2 flex-wrap">
          {objet.besoinAchat && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded">
              🛒
            </span>
          )}
          {objet.sku && (
            <span className="text-xs text-gray-400 font-mono truncate max-w-full">{objet.sku}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjetCardMobile;
